import React, { useEffect } from 'react';
import { Funnel } from 'lucide-react';
import { AppSelect } from '../select';
import { PERIOD_OPTIONS, getPeriodDateRange } from './periodUtils';

/**
 * AdvancedFilterPanel
 *
 * A full Bootstrap offcanvas filter panel with:
 *   1. Period preset selector (always at top)
 *   2. Configurable filter fields (select, multiselect, dateRange)
 *   3. Reset / Apply footer
 *
 * Usage:
 *   <AdvancedFilterPanel
 *     offcanvasId="offcanvasCompany"
 *     filterData={filterData}
 *     onChange={(key, val) => setFilterData(prev => ({ ...prev, [key]: val }))}
 *     onApply={handleApply}
 *     onReset={handleReset}
 *     filterFields={[
 *       { key: 'status',    label: 'Status',    type: 'select',      options: [...] },
 *       { key: 'branchId',  label: 'Branch',    type: 'multiselect', options: [...] },
 *       { key: 'billDate',  label: 'Bill Date', type: 'dateRange',   fromKey: 'billDateFrom', toKey: 'billDateTo' },
 *     ]}
 *   />
 *
 * filterData must contain: { period, startDate, endDate, ...customFields }
 * filterFields[*].type: 'select' | 'multiselect' | 'dateRange'
 */
/**
 * FilterButton
 *
 * Drop-in trigger button for AdvancedFilterPanel.
 *
 * Usage:
 *   <FilterButton offcanvasId="offcanvasCompany" activeCount={activeFilterCount} />
 */
export const FilterButton = ({ offcanvasId, activeCount = 0, label = 'Filter', onClick }) => (
  <button
    type="button"
    className="btn btn-outline-secondary position-relative"
    data-bs-toggle="offcanvas"
    data-bs-target={`#${offcanvasId}`}
    onClick={onClick}
  >
    <Funnel size={16} className="me-1" />
    {label}
    {activeCount > 0 && (
      <span
        className="badge bg-primary rounded-pill position-absolute"
        style={{ top: '-6px', right: '-8px', fontSize: '0.65rem', minWidth: '18px', padding: '2px 5px' }}
      >
        {activeCount}
      </span>
    )}
  </button>
);

const AdvancedFilterPanel = ({
  offcanvasId,
  title = 'Advanced Filter',
  filterData = {},
  onChange,
  onApply,
  onReset,
  filterFields = [],
  showPeriod = true,
  removeBackdropOnOpen = false,
}) => {
  // Restore body scroll-lock that Bootstrap offcanvas leaves behind
  useEffect(() => {
    const el = document.getElementById(offcanvasId);
    if (!el) return;
    const onShow = () => {
      document.querySelectorAll('.offcanvas-backdrop').forEach(b => b.remove());
    };
    const onHidden = () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.body.classList.remove('offcanvas-open');
    };
    el.addEventListener('show.bs.offcanvas', onShow);
    el.addEventListener('hidden.bs.offcanvas', onHidden);
    return () => {
      el.removeEventListener('show.bs.offcanvas', onShow);
      el.removeEventListener('hidden.bs.offcanvas', onHidden);
    };
  }, [offcanvasId]);

  const handlePeriodSelect = (periodValue) => {
    const newPeriod = filterData.period === periodValue ? '' : periodValue;
    onChange('period', newPeriod);
    if (newPeriod && newPeriod !== 'custom') {
      // Clear manual date inputs when a preset is selected
      onChange('startDate', '');
      onChange('endDate', '');
    }
  };

  const isCustomOrNoPeriod = !filterData.period || filterData.period === 'custom';
  const presetRange =
    filterData.period && filterData.period !== 'custom'
      ? getPeriodDateRange(filterData.period)
      : null;

  const renderField = (field) => {
    if (field.type === 'dateRange') {
      return (
        <div className="mb-3" key={`${field.fromKey}_${field.toKey}`}>
          <label className="form-label fw-medium">{field.label}</label>
          <div className="row g-2">
            <div className="col">
              <label className="form-label small text-muted mb-1">From</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={filterData[field.fromKey] || ''}
                onChange={(e) => onChange(field.fromKey, e.target.value)}
              />
            </div>
            <div className="col">
              <label className="form-label small text-muted mb-1">To</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={filterData[field.toKey] || ''}
                onChange={(e) => onChange(field.toKey, e.target.value)}
              />
            </div>
          </div>
        </div>
      );
    }

    if (field.type === 'text') {
      return (
        <div className="mb-3" key={field.key}>
          <label className="form-label fw-medium">{field.label}</label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={filterData[field.key] || ''}
            placeholder={field.placeholder || `Search ${field.label}`}
            onChange={(e) => onChange(field.key, e.target.value)}
            disabled={field.disabled || false}
          />
        </div>
      );
    }

    const isMulti = field.type === 'multiselect';
    const opts = field.options || [];
    const val = filterData[field.key];

    return (
      <div className="mb-3" key={field.key}>
        <label className="form-label fw-medium">{field.label}</label>
        <AppSelect
          name={field.key}
          value={val !== undefined && val !== null ? val : (isMulti ? [] : '')}
          onChange={(n, v) => onChange(field.key, v)}
          options={opts}
          placeholder={field.placeholder || `All ${field.label}`}
          valueField={field.valueField || 'value'}
          labelField={field.labelField || 'label'}
          searchField={field.searchField || field.labelField || 'label'}
          multiple={isMulti}
          required={false}
          create={false}
          disabled={field.disabled || false}
          dropdownParent={null}
        />
      </div>
    );
  };

  return (
    <div
      className="offcanvas offcanvas-end crm-advanced-filter-offcanvas"
      tabIndex="-1"
      id={offcanvasId}
      aria-labelledby={`${offcanvasId}Label`}
    >
      {/* HEADER */}
      <div className="offcanvas-header border-bottom">
        <h5 className="offcanvas-title d-flex align-items-center gap-2" id={`${offcanvasId}Label`}>
          <svg
            xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            className="text-primary"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M4 4h16v2.172a2 2 0 0 1-.586 1.414l-4.414 4.414v7l-6 2v-8.5l-4.48-4.928a2 2 0 0 1-.52-1.345v-2.227z" />
          </svg>
          {title}
        </h5>
        <button
          type="button"
          className="btn-close text-reset"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
        />
      </div>

      {/* BODY */}
      <div className="offcanvas-body" style={{ overflowY: 'auto', paddingBottom: '80px' }}>

        {/* ── PERIOD FILTER (always first) ── */}
        {showPeriod && (
          <div className="mb-4">
            <label className="form-label fw-semibold d-flex align-items-center gap-1 mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              Period
            </label>

            {/* Preset pill buttons */}
            <div className="d-flex flex-wrap gap-1 mb-2">
              {PERIOD_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`btn btn-sm px-2 py-1 ${
                    filterData.period === opt.value ? 'btn-primary' : 'btn-outline-secondary'
                  }`}
                  style={{ fontSize: '0.72rem', lineHeight: '1.4' }}
                  onClick={() => handlePeriodSelect(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Custom date inputs — shown when "Custom Range" selected or no period */}
            {isCustomOrNoPeriod && (
              <div className="row g-2 mt-1">
                <div className="col">
                  <label className="form-label small text-muted mb-1">From</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={filterData.startDate || ''}
                    onChange={(e) => onChange('startDate', e.target.value)}
                  />
                </div>
                <div className="col">
                  <label className="form-label small text-muted mb-1">To</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={filterData.endDate || ''}
                    onChange={(e) => onChange('endDate', e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Computed range display for presets */}
            {presetRange && (
              <div className="mt-2">
                <small className="text-muted d-flex align-items-center gap-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  {presetRange.startDate} → {presetRange.endDate}
                </small>
              </div>
            )}
          </div>
        )}

        {/* ── CUSTOM FILTER FIELDS ── */}
        {filterFields.map(renderField)}
      </div>

      {/* FOOTER */}
      <div
        className="border-top p-3"
        style={{ position: 'absolute', bottom: 0, left: 0, right: 0, backgroundColor: '#fff', zIndex: 10 }}
      >
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary flex-fill"
            onClick={onReset}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              className="me-1">
              <polyline points="1 4 1 10 7 10" />
              <path d="M3.51 15a9 9 0 1 0 .49-3.51" />
            </svg>
            Reset
          </button>
          <button
            type="button"
            className="btn btn-primary flex-fill"
            data-bs-dismiss="offcanvas"
            onClick={onApply}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              className="me-1">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Apply Filter
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdvancedFilterPanel;
